/**
 * src/store/slices/reportsSlice.js
 *
 * Redux slice for the entire Reports module.
 *
 * STATE SHAPE:
 * {
 *   overview: { data: null, loading: false, error: null },
 *   projects: { data: [], loading: false, error: null },
 *   projectSummary:   { data: null, loading: false, error: null },
 *   projectStatus:    { data: [], pagination: null, loading: false, error: null },
 *   projectTimesheet: { data: [], pagination: null, loading: false, error: null },
 *   projectRisk:      { data: [], pagination: null, loading: false, error: null },
 *   taskDetail:       { data: null, loading: false, error: null },
 *   leaveResources:   { data: [], loading: false, error: null },
 *   leaveReport:      { data: [], pagination: null, loading: false, error: null },
 *   downloadLoading: false,
 * }
 */

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import reportsService from "../../services/reportsService";
import projectService from "../../services/projectService";
import dashboardService from "../../services/dashboardService";

// ─── helper: trigger a browser file download from a blob response ───
const triggerDownload = (response, filename) => {
  const url  = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href  = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const unwrapData = (response) => response?.data ?? response ?? [];

const clampPct = (value) => Math.max(0, Math.min(100, Number(value) || 0));

const isTaskDone = (task) =>
  task?.status === "done" ||
  task?.status === "completed" ||
  task?.completed === true ||
  task?.isCompleted === true;

const getSubtaskCounts = (task) => {
  const subtasks = Array.isArray(task?.subtasks) ? task.subtasks : [];
  const total = task?.subtasksTotal ?? task?.totalSubtasks ?? subtasks.length;
  const completed =
    task?.subtasksDone ??
    task?.completedSubtasks ??
    subtasks.filter((subtask) => subtask?.isCompleted || subtask?.completed || subtask?.status === "done").length;

  return { completed: Number(completed) || 0, total: Number(total) || 0 };
};

const getTaskCompletion = (task) => {
  if (task?.completion != null) return clampPct(task.completion);
  if (task?.progress != null) return clampPct(task.progress);
  if (isTaskDone(task)) return 100;

  const { completed, total } = getSubtaskCounts(task);
  if (total > 0) return Math.round((completed / total) * 100);

  return 0;
};

const getDueDate = (task) =>
  task?.dueDate || task?.taskDeadline || task?.endDate || task?.deadline || null;

const getStartDate = (task) =>
  task?.startDate || task?.taskStartDate || task?.createdAt || null;

const getStatusLabel = (task) => {
  if (isTaskDone(task)) return "Completed";
  const dueDate = getDueDate(task);
  if (dueDate && new Date(dueDate) < new Date()) return "At Risk";
  return "On Track";
};

const getDaysOverdue = (task) => {
  const dueDate = getDueDate(task);
  if (!dueDate || isTaskDone(task)) return 0;

  const end = new Date(dueDate);
  const today = new Date();
  if (Number.isNaN(end.getTime()) || end >= today) return 0;

  return Math.max(1, Math.ceil((today - end) / (1000 * 60 * 60 * 24)));
};

const normalizeAssignees = (task) =>
  task?.resources || task?.assignees || task?.assignedTo || [];

const normalizeTask = (task, list = {}, project = {}) => {
  const { completed, total } = getSubtaskCounts(task);
  const status = getStatusLabel(task);
  const dueDate = getDueDate(task);

  return {
    ...task,
    _id: task?._id || task?.id,
    projectId: project?._id || project?.id,
    projectName: project?.name,
    listId: list?._id || list?.id,
    listName: list?.name || task?.listName || "-",
    name: task?.name || task?.task || "Untitled Task",
    resources: normalizeAssignees(task),
    dueDate,
    startDate: getStartDate(task),
    status,
    completion: getTaskCompletion(task),
    subtasksDone: completed,
    subtasksTotal: total,
    loggedHours: task?.loggedHours ?? task?.hoursLogged ?? task?.actualHours ?? 0,
    estimatedHours: task?.estimatedHours ?? task?.hours ?? 0,
    daysOverdue: getDaysOverdue(task),
    daysOverdueDisplay: `${getDaysOverdue(task)} Days`,
    impact: task?.impact || (task?.priority || "").toUpperCase() || "LOW",
  };
};

const flattenProjectTasks = (lists = [], project = {}) =>
  lists.flatMap((list) =>
    (Array.isArray(list?.tasks) ? list.tasks : []).map((task) =>
      normalizeTask(task, list, project),
    ),
  );

const paginate = (rows, page = 1, limit = 5) => {
  const safePage = Math.max(1, Number(page) || 1);
  const safeLimit = Math.max(1, Number(limit) || 5);
  const totalResults = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalResults / safeLimit));
  const currentPage = Math.min(safePage, totalPages);
  const fromIndex = (currentPage - 1) * safeLimit;
  const data = rows.slice(fromIndex, fromIndex + safeLimit);

  return {
    data,
    pagination: {
      page: currentPage,
      limit: safeLimit,
      totalPages,
      totalResults,
      showing: {
        from: totalResults ? fromIndex + 1 : 0,
        to: fromIndex + data.length,
      },
    },
  };
};

const getProjectTasks = async (projectId, project = {}) => {
  const res = await projectService.getProjectLists(projectId);
  return flattenProjectTasks(unwrapData(res), project);
};

// ════════════════════════════════════════════════════════════════════
// ASYNC THUNKS
// ════════════════════════════════════════════════════════════════════

export const fetchReportsOverview = createAsyncThunk(
  "reports/fetchOverview",
  async (_, { rejectWithValue }) => {
    try {
      // Never let one optional report request turn every card into zero. The
      // dashboard source is authoritative for shared project figures, while
      // /reports/overview supplies report-only figures when it is available.
      const [dashboardResult, projectResult, reportsResult] = await Promise.allSettled([
        dashboardService.getDashboard(),
        dashboardService.getDashboardProjects(),
        reportsService.getOverview(),
      ]);
      const dashboard = dashboardResult.status === "fulfilled"
        ? unwrapData(dashboardResult.value) || {}
        : {};
      const dashboardStats = dashboard.stats || {};
      const reportStats = reportsResult.status === "fulfilled"
        ? unwrapData(reportsResult.value) || {}
        : {};
      const projects = projectResult.status === "fulfilled"
        ? unwrapData(projectResult.value)
        : [];

      if (!Object.keys(dashboardStats).length && !Object.keys(reportStats).length && !projects.length) {
        throw new Error("Unable to load report overview");
      }

      const taskLists = await Promise.all(
        projects.map(async (project) => {
          try {
            return await getProjectTasks(project._id || project.id, project);
          } catch {
            return [];
          }
        }),
      );
      const tasks = taskLists.flat();
      const atRiskTasks = tasks.filter((task) => task.status === "At Risk").length;

      return {
        totalProjects: dashboardStats.totalProjects != null && Number.isFinite(Number(dashboardStats.totalProjects))
          ? Number(dashboardStats.totalProjects)
          : reportStats.totalProjects ?? projects.length,
        totalTasks: dashboardStats.totalTasks ?? reportStats.totalTasks ?? tasks.length,
        pendingTasks: dashboardStats.pendingTasks ?? reportStats.pendingTasks ?? tasks.filter((task) => task.status !== "Completed").length,
        atRiskTasks: dashboardStats.atRiskTasks ?? reportStats.atRiskTasks ?? atRiskTasks,
        // Dashboard's At Risk card is project-based (`stats.atRisk`). Keep
        // that authoritative value so Reports never disagrees with it.
        atRiskProjects: dashboardStats.atRisk ?? dashboardStats.atRiskProjects ?? reportStats.atRiskProjects ?? projects.filter((project) => project.trackingStatus === "at-risk").length,
        timesheetDelay: dashboardStats.timesheetDelay ?? reportStats.timesheetDelay ?? tasks.filter((task) => !Number(task.loggedHours)).length,
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load overview");
    }
  }
);

export const fetchReportProjects = createAsyncThunk(
  "reports/fetchProjects",
  async (_, { rejectWithValue }) => {
    try {
      const res = await dashboardService.getDashboardProjects();
      return unwrapData(res); // same project collection used by Dashboard
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load projects");
    }
  }
);

export const fetchProjectSummary = createAsyncThunk(
  "reports/fetchProjectSummary",
  async (projectId, { rejectWithValue }) => {
    try {
      const projectRes = await projectService.getProjectById(projectId);
      const project = unwrapData(projectRes);
      const summaryRes = await projectService.getProjectSummary(projectId).catch(() => null);
      const summary = unwrapData(summaryRes) || {};
      const tasks = await getProjectTasks(projectId, { ...project, ...summary });
      const completedTasks = tasks.filter((task) => task.status === "Completed").length;
      const totalTasks = tasks.length;
      const members = summary?.members || project?.members || [];

      return {
        ...project,
        ...summary,
        totalTasks,
        completedTasks,
        pendingTasks: tasks.filter((task) => task.status !== "Completed").length,
        atRiskTasks: tasks.filter((task) => task.status === "At Risk").length,
        resourceCount: members.length || new Set(tasks.flatMap((task) => task.resources || []).map((user) => user?._id || user?.id || user)).size,
        completionPct: totalTasks ? Math.round((completedTasks / totalTasks) * 100) : clampPct(summary?.progress || project?.progress),
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load project summary");
    }
  }
);

export const fetchProjectStatus = createAsyncThunk(
  "reports/fetchProjectStatus",
  async ({ projectId, page = 1, limit = 5 }, { rejectWithValue }) => {
    try {
      const tasks = await getProjectTasks(projectId);
      return paginate(tasks, page, limit);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load status tab");
    }
  }
);

export const fetchProjectTimesheet = createAsyncThunk(
  "reports/fetchProjectTimesheet",
  async ({ projectId, page = 1, limit = 5, startDate = "", endDate = "" }, { rejectWithValue }) => {
    try {
      const tasks = await getProjectTasks(projectId);
      return paginate(tasks, page, limit);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load timesheet tab");
    }
  }
);

export const fetchProjectRisk = createAsyncThunk(
  "reports/fetchProjectRisk",
  async ({ projectId, page = 1, limit = 5 }, { rejectWithValue }) => {
    try {
      const tasks = await getProjectTasks(projectId);
      return paginate(tasks.filter((task) => task.status === "At Risk"), page, limit);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load risk tab");
    }
  }
);

export const fetchTaskDetail = createAsyncThunk(
  "reports/fetchTaskDetail",
  async ({ projectId, taskId }, { rejectWithValue }) => {
    try {
      const tasks = await getProjectTasks(projectId);
      const task = tasks.find((item) => String(item._id || item.id) === String(taskId));
      if (!task) throw new Error("Task not found");
      return task;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load task detail");
    }
  }
);

export const fetchLeaveResources = createAsyncThunk(
  "reports/fetchLeaveResources",
  async (_, { rejectWithValue }) => {
    try {
      const res = await reportsService.getLeaveResources();
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load resources");
    }
  }
);

export const fetchLeaveReport = createAsyncThunk(
  "reports/fetchLeaveReport",
  async ({ year, page = 1, limit = 5, userId = "" }, { rejectWithValue }) => {
    try {
      const res = await reportsService.getLeaveReport(year, page, limit, userId);
      return { data: res.data, pagination: res.pagination };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to load leave report");
    }
  }
);

export const downloadProjectReport = createAsyncThunk(
  "reports/downloadProjectReport",
  async ({ projectId, tab, startDate = "", endDate = "" }, { rejectWithValue }) => {
    try {
      const res = await reportsService.downloadProjectReport(projectId, tab, startDate, endDate);
      triggerDownload(res, `project_report_${tab}.csv`);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Download failed");
    }
  }
);

export const downloadLeaveReport = createAsyncThunk(
  "reports/downloadLeaveReport",
  async ({ year, userId = "" }, { rejectWithValue }) => {
    try {
      const res = await reportsService.downloadLeaveReport(year, userId);
      triggerDownload(res, `leave_report_${year}.csv`);
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Download failed");
    }
  }
);

// ════════════════════════════════════════════════════════════════════
// SLICE
// ════════════════════════════════════════════════════════════════════

const initialState = {
  overview: { data: null, loading: false, error: null },
  projects: { data: [],   loading: false, error: null },

  projectSummary:   { data: null, loading: false, error: null },
  projectStatus:    { data: [], pagination: null, loading: false, error: null },
  projectTimesheet: { data: [], pagination: null, loading: false, error: null },
  projectRisk:      { data: [], pagination: null, loading: false, error: null },
  taskDetail:       { data: null, loading: false, error: null },

  leaveResources: { data: [], loading: false, error: null },
  leaveReport:    { data: [], pagination: null, loading: false, error: null },

  downloadLoading: false,
};

// ─── small helper to build the 3 case-reducers for any async thunk ──
const asyncCases = (builder, thunk, stateKey) => {
  builder
    .addCase(thunk.pending, (state) => {
      state[stateKey].loading = true;
      state[stateKey].error   = null;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[stateKey].loading = false;
      // payload may be { data, pagination } or just the array/object
      if (action.payload && typeof action.payload === "object" && "data" in action.payload) {
        state[stateKey].data       = action.payload.data;
        state[stateKey].pagination = action.payload.pagination ?? null;
      } else {
        state[stateKey].data = action.payload;
      }
    })
    .addCase(thunk.rejected, (state, action) => {
      state[stateKey].loading = false;
      state[stateKey].error   = action.payload;
    });
};

const reportsSlice = createSlice({
  name: "reports",
  initialState,

  reducers: {
    // Call this when the user navigates away from a project detail
    // so stale data doesn't flash on the next project
    clearProjectDetail(state) {
      state.projectSummary   = initialState.projectSummary;
      state.projectStatus    = initialState.projectStatus;
      state.projectTimesheet = initialState.projectTimesheet;
      state.projectRisk      = initialState.projectRisk;
      state.taskDetail       = initialState.taskDetail;
    },
    clearTaskDetail(state) {
      state.taskDetail = initialState.taskDetail;
    },
    clearLeaveReport(state) {
      state.leaveReport = initialState.leaveReport;
    },
  },

  extraReducers: (builder) => {
    asyncCases(builder, fetchReportsOverview,  "overview");
    asyncCases(builder, fetchReportProjects,   "projects");
    asyncCases(builder, fetchProjectSummary,   "projectSummary");
    asyncCases(builder, fetchProjectStatus,    "projectStatus");
    asyncCases(builder, fetchProjectTimesheet, "projectTimesheet");
    asyncCases(builder, fetchProjectRisk,      "projectRisk");
    asyncCases(builder, fetchTaskDetail,       "taskDetail");
    asyncCases(builder, fetchLeaveResources,   "leaveResources");
    asyncCases(builder, fetchLeaveReport,      "leaveReport");

    // Download thunks — just track a single loading flag
    builder
      .addCase(downloadProjectReport.pending,  (state) => { state.downloadLoading = true; })
      .addCase(downloadProjectReport.fulfilled,(state) => { state.downloadLoading = false; })
      .addCase(downloadProjectReport.rejected, (state) => { state.downloadLoading = false; })
      .addCase(downloadLeaveReport.pending,    (state) => { state.downloadLoading = true; })
      .addCase(downloadLeaveReport.fulfilled,  (state) => { state.downloadLoading = false; })
      .addCase(downloadLeaveReport.rejected,   (state) => { state.downloadLoading = false; });
  },
});

export const {
  clearProjectDetail,
  clearTaskDetail,
  clearLeaveReport,
} = reportsSlice.actions;

export default reportsSlice.reducer;
